import { useEffect, useState } from "react";
import { format } from "date-fns";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { IndianRupee, ReceiptText, Percent, Tag, Download, CalendarDays } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const PERIODS = [
  { id: "day", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "year", label: "This Year" },
];

const fmtRs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function SalesReport() {
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const customActive = !!(range?.from && range?.to);
  const rangeLabel = customActive
    ? `${format(range.from, "dd MMM")} → ${format(range.to, "dd MMM yyyy")}`
    : range?.from
      ? `${format(range.from, "dd MMM yyyy")} → …`
      : "Custom range";

  useEffect(() => {
    if (range && !(range.from && range.to)) return;
    setLoading(true);
    const params = customActive
      ? { from_date: format(range.from, "yyyy-MM-dd"), to_date: format(range.to, "yyyy-MM-dd") }
      : { period };
    api.get("/reports/sales", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [period, range]);

  const pickPeriod = (id) => {
    setRange(null);
    setPeriod(id);
  };

  return (
    <div className="space-y-5" data-testid="sale-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Sale Report</h1>
          <p className="text-sm text-slate-500">All sales — filter by day, week, month or year.</p>
        </div>
        {data && (
          <Button variant="outline" data-testid="export-sales-btn" onClick={() => window.open(`${API_BASE}/export/sales?from_date=${data.from.slice(0, 10)}&to_date=${new Date().toISOString().slice(0, 10)}`, "_blank")}>
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap items-center" data-testid="period-tabs">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            data-testid={`period-tab-${p.id}`}
            onClick={() => pickPeriod(p.id)}
            className={`px-4 h-10 rounded-lg text-sm font-semibold border transition-all ${!customActive && period === p.id ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}
          >
            {p.label}
          </button>
        ))}
        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="sales-calendar-btn"
              className={`px-4 h-10 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2 ${customActive ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}
            >
              <CalendarDays className="w-4 h-4" />
              {rangeLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white" align="start">
            <Calendar
              data-testid="sales-calendar"
              mode="range"
              selected={range}
              onSelect={(r) => r && setRange(r)}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
            />
          </PopoverContent>
        </Popover>
      </div>

      {loading || !data ? (
        <div className="text-slate-500">Loading report…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi tid="sr-revenue" icon={IndianRupee} label="Total Sale" value={fmtRs(data.revenue)} />
            <Kpi tid="sr-invoices" icon={ReceiptText} label="Bills" value={data.invoice_count} />
            <Kpi tid="sr-gst" icon={Percent} label="GST Collected" value={fmtRs(data.gst_total)} />
            <Kpi tid="sr-discount" icon={Tag} label="Discount Given" value={fmtRs(data.discount_total)} />
          </div>

          <Card className="p-6 border-slate-200">
            <h3 className="font-display font-bold">Daily Sale Breakdown</h3>
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.daily}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8 }} />
                  <Bar dataKey="revenue" fill="#0F5132" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="border-slate-200">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-display font-bold">Sales List ({data.invoice_count})</h3>
              <p className="text-xs text-slate-500">Since {new Date(data.from).toLocaleDateString("en-IN")}</p>
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="sales-table">
                <TableHeader><TableRow>
                  <TableHead>Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Date</TableHead>
                  <TableHead>Items</TableHead><TableHead>Payment</TableHead><TableHead className="text-right">Total</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`sale-row-${inv.id}`}>
                      <TableCell className="font-mono-num font-bold">{inv.invoice_no}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{inv.customer_name}</div>
                        <div className="text-xs text-slate-500">{inv.customer_phone}</div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{new Date(inv.created_at).toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-sm">{inv.items.length}</TableCell>
                      <TableCell><Badge variant="outline" className="uppercase text-xs">{inv.payment_mode}</Badge></TableCell>
                      <TableCell className="text-right font-mono-num font-bold text-emerald-900">₹{inv.grand_total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  {data.invoices.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No sales in this period</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tid }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="font-mono-num text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-700" />
        </div>
      </div>
    </Card>
  );
}
